import { Box, Grid, Text } from '@chakra-ui/layout';
import { Link, SkeletonText } from '@chakra-ui/react';
import { startCase } from 'lodash';
import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';

import { Loading } from '@revelio/core';
import { countBulletLines } from '@revelio/iso-utility';
import { FormatType, getFormatter } from '@revelio/replots';

import { useTrialUserBlurLogic } from '../../../hooks/useTrialUserBlurLogic';
import { normalizeProfileUrl } from '../../../utils/normalizeProfileUrl';
import BlurredCell from '../../BlurredCell';
import styles from './FullProfileInfo.module.css';
import type { FullProfileInfoData, SalaryRange } from './types';
import { useFormattedWorkExperience } from './useFormattedWorkExperience';

const GRID_COLUMN_GAP = 25;
const RIGHT_COLUMN_MIN_WIDTH = 180;
const GRID_TEMPLATE_COLUMNS = `minmax(0, calc(100% - ${RIGHT_COLUMN_MIN_WIDTH}px)) minmax(${RIGHT_COLUMN_MIN_WIDTH}px, 1fr)`;
const salaryFormatter = getFormatter(FormatType.CURRENCY);

const getPillClassName = (value: string): string => {
  const normalized = value.toLowerCase();
  if (normalized.includes('high') || normalized.includes('very high')) {
    return `${styles.pill} ${styles.pillHigh}`;
  }
  if (normalized.includes('medium') || normalized.includes('moderate')) {
    return `${styles.pill} ${styles.pillMedium}`;
  }
  if (normalized.includes('low') || normalized.includes('very low')) {
    return `${styles.pill} ${styles.pillLow}`;
  }
  return '';
};

const renderValue = (value: string): React.ReactNode => {
  const pillClassName = getPillClassName(value);
  const displayValue = startCase(value);

  if (pillClassName) {
    return <Text className={pillClassName}>{displayValue}</Text>;
  }

  return <Text className={styles.enrichedDataValue}>{displayValue}</Text>;
};

const hasMarkdownContent = (children: React.ReactNode): boolean =>
  React.Children.toArray(children).some((child) => {
    if (typeof child === 'string') {
      return child.trim().length > 0;
    }
    if (typeof child === 'number') {
      return true;
    }
    if (React.isValidElement<{ children?: React.ReactNode }>(child)) {
      const nested = child.props.children;
      return nested ? hasMarkdownContent(nested) : false;
    }
    return false;
  });

const renderMarkdownDescription = (description: string): React.ReactElement => (
  <Box className={styles.markdownContent}>
    <ReactMarkdown
      components={{
        p: ({ children }) => {
          if (!hasMarkdownContent(children)) {
            return null;
          }
          return (
            <Text
              as="p"
              className={`${styles.sectionDetails} ${styles.markdownParagraph}`}
            >
              {children}
            </Text>
          );
        },
        ul: ({ children }) => (
          <Box as="ul" className={styles.markdownList}>
            {children}
          </Box>
        ),
        li: ({ children }) => (
          <Text as="li" className={styles.markdownListItem}>
            {children}
          </Text>
        ),
      }}
    >
      {description.trimEnd()}
    </ReactMarkdown>
  </Box>
);

const shouldRenderMarkdownDescription = (
  description: string,
  isFormatted: boolean
): boolean => {
  if (isFormatted) {
    return true;
  }
  return countBulletLines(description) > 0;
};

type NameParts = {
  firstName: string;
  lastName: string;
  fullName: string;
};

const getHeaderNameParts = (
  header: FullProfileInfoData['header']
): NameParts => {
  const providedFirstName = header.firstName?.trim() ?? '';
  const providedLastName = header.lastName?.trim() ?? '';

  if (providedFirstName || providedLastName) {
    return {
      firstName: providedFirstName,
      lastName: providedLastName,
      fullName: [providedFirstName, providedLastName].filter(Boolean).join(' '),
    };
  }

  const trimmedName = header.name.trim();
  if (!trimmedName) {
    return { firstName: '', lastName: '', fullName: '' };
  }

  const nameParts = trimmedName.split(/\s+/);
  if (nameParts.length === 1) {
    return {
      firstName: nameParts[0] ?? '',
      lastName: '',
      fullName: trimmedName,
    };
  }

  return {
    firstName: nameParts.slice(0, -1).join(' '),
    lastName: nameParts[nameParts.length - 1] ?? '',
    fullName: trimmedName,
  };
};

type EmailRenderProps = {
  email: string;
  isRestricted: boolean;
  encryptedValue?: string;
  onMouseEnter: () => void;
  userKey?: string;
  fieldKey: string;
};

const renderEmailLink = ({
  email,
  isRestricted,
  encryptedValue,
  onMouseEnter,
  userKey,
  fieldKey,
}: EmailRenderProps): React.ReactNode => {
  const trimmedEmail = email.trim();
  if (!trimmedEmail) {
    return <Text className={styles.sectionDetails}>{email}</Text>;
  }

  if (isRestricted) {
    return (
      <Box className={styles.sectionDetails}>
        <BlurredCell
          value={trimmedEmail}
          encryptedValue={encryptedValue}
          onMouseEnter={onMouseEnter}
          allowUnblur={false}
          isCompact={false}
          userKey={userKey}
          fieldKey={fieldKey}
        />
      </Box>
    );
  }

  return (
    <Link
      as="a"
      href={`mailto:${trimmedEmail}`}
      color="lightBlue.600"
      fontSize="12px"
      _hover={{ textDecoration: 'underline' }}
      display="block"
    >
      {trimmedEmail}
    </Link>
  );
};

type PhoneRenderProps = {
  phone: string;
  isRestricted: boolean;
  encryptedValue?: string;
  onMouseEnter: () => void;
  userKey?: string;
  fieldKey: string;
};

const renderPhoneValue = ({
  phone,
  isRestricted,
  encryptedValue,
  onMouseEnter,
  userKey,
  fieldKey,
}: PhoneRenderProps): React.ReactNode => {
  if (!phone.trim()) {
    return <Text className={styles.sectionDetails}>{phone}</Text>;
  }

  if (isRestricted) {
    return (
      <Box className={styles.sectionDetails}>
        <BlurredCell
          value={phone}
          encryptedValue={encryptedValue}
          onMouseEnter={onMouseEnter}
          allowUnblur={false}
          isCompact={false}
          userKey={userKey}
          fieldKey={fieldKey}
        />
      </Box>
    );
  }

  return <Text className={styles.sectionDetails}>{phone}</Text>;
};

type ProfileUrlRenderProps = {
  profileUrl: string;
  isRestricted: boolean;
  encryptedValue?: string;
  onMouseEnter: () => void;
  userKey?: string;
  fieldKey: string;
};

const renderProfileUrl = ({
  profileUrl,
  isRestricted,
  encryptedValue,
  onMouseEnter,
  userKey,
  fieldKey,
}: ProfileUrlRenderProps): React.ReactNode => {
  const normalized = normalizeProfileUrl(profileUrl);
  if (!normalized) {
    return null;
  }

  if (isRestricted) {
    return (
      <Box className={styles.sectionDetails}>
        <BlurredCell
          value={normalized.displayValue}
          encryptedValue={encryptedValue}
          onMouseEnter={onMouseEnter}
          allowUnblur={false}
          isCompact={false}
          userKey={userKey}
          fieldKey={fieldKey}
        />
      </Box>
    );
  }

  return (
    <Link
      as="a"
      href={normalized.href}
      color="lightBlue.600"
      fontSize="12px"
      _hover={{ textDecoration: 'underline' }}
      display="block"
      isExternal
      rel="noopener noreferrer"
    >
      {normalized.displayValue}
    </Link>
  );
};

const formatSalaryRange = ({ min, max }: SalaryRange): string => {
  if (min === max) {
    return salaryFormatter(min);
  }

  return `${salaryFormatter(min)} - ${salaryFormatter(max)}`;
};

interface FullProfileInfoProps {
  data: FullProfileInfoData;
}

export const FullProfileInfoLoading = ({
  minHeight = '400px',
}: {
  minHeight?: number | string;
}) => {
  return (
    <Box
      className={styles.loadingContainer}
      minHeight={minHeight}
      width="100%"
      height="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      data-testid="full-profile-info-loading"
    >
      <Loading size="lg" />
    </Box>
  );
};

export const FullProfileInfo = ({ data }: FullProfileInfoProps) => {
  const { workExperience: formattedWorkExperience, formattingIndices } =
    useFormattedWorkExperience(data.workExperience);
  const { isTrialUser, showBlurToast } = useTrialUserBlurLogic();
  const userKey = data.originalUserId ?? data.userId;
  const maskedHeader = useMemo(() => {
    if (!isTrialUser) {
      return data.header;
    }

    const maskedValues = data.header.maskedValues ?? {};
    return {
      ...data.header,
      lastName: maskedValues.lastName ?? data.header.lastName ?? '',
      workEmail: maskedValues.workEmail ?? data.header.workEmail,
      personalEmail: maskedValues.personalEmail ?? data.header.personalEmail,
      phone: maskedValues.phone ?? data.header.phone,
      profileUrl: maskedValues.profileUrl ?? data.header.profileUrl,
    };
  }, [data.header, isTrialUser]);

  const dataWithFormattedDescriptions = {
    ...data,
    workExperience: formattedWorkExperience,
  };
  const nameParts = getHeaderNameParts(maskedHeader);
  const shouldBlurLastName = isTrialUser && nameParts.lastName.length > 0;

  return (
    <Box className={styles.container}>
      {/* Top Section - Two Column Grid */}
      <Grid
        gridColumnGap={`${GRID_COLUMN_GAP}px`}
        gridRowGap={0}
        templateColumns={GRID_TEMPLATE_COLUMNS}
        width="100%"
        alignItems="start"
        marginTop={0}
        paddingTop={0}
        margin={0}
        padding={0}
      >
        {/* Header Info */}
        <Box className={styles.contentBox}>
          {shouldBlurLastName ? (
            <Box
              className={styles.name}
              display="flex"
              alignItems="baseline"
              columnGap="6px"
            >
              {nameParts.firstName && <span>{nameParts.firstName}</span>}
              <Box display="inline-block">
                <BlurredCell
                  value={nameParts.lastName}
                  encryptedValue={maskedHeader.originalValues?.lastName}
                  onMouseEnter={showBlurToast}
                  allowUnblur={false}
                  isCompact={false}
                  userKey={userKey}
                  fieldKey="last_name"
                />
              </Box>
            </Box>
          ) : (
            <Text className={styles.name}>{nameParts.fullName}</Text>
          )}
          <Text className={styles.role}>{maskedHeader.role}</Text>
          {renderPhoneValue({
            phone: maskedHeader.phone,
            isRestricted: isTrialUser,
            encryptedValue: maskedHeader.originalValues?.phone,
            onMouseEnter: showBlurToast,
            userKey,
            fieldKey: 'phone_number',
          })}
          {renderEmailLink({
            email: maskedHeader.workEmail,
            isRestricted: isTrialUser,
            encryptedValue: maskedHeader.originalValues?.workEmail,
            onMouseEnter: showBlurToast,
            userKey,
            fieldKey: 'email',
          })}
          <Text className={styles.sectionDetails}>{maskedHeader.location}</Text>
          {renderEmailLink({
            email: maskedHeader.personalEmail,
            isRestricted: isTrialUser,
            encryptedValue: maskedHeader.originalValues?.personalEmail,
            onMouseEnter: showBlurToast,
            userKey,
            fieldKey: 'personal_email',
          })}
          {maskedHeader.profileUrl.trim().length > 0 &&
            renderProfileUrl({
              profileUrl: maskedHeader.profileUrl,
              isRestricted: isTrialUser,
              encryptedValue: maskedHeader.originalValues?.profileUrl,
              onMouseEnter: showBlurToast,
              userKey,
              fieldKey: 'linkedin_url',
            })}
        </Box>

        {/* Metadata (Gray Card) */}
        <Box className={styles.grayCard}>
          {(data.metadata.salary.min > 0 || data.metadata.salary.max > 0) && (
            <Box className={styles.enrichedData}>
              <Text className={styles.enrichedDataLabel}>Salary</Text>
              <Text
                className={styles.enrichedDataValuePlain}
                color="text.primary"
              >
                {formatSalaryRange(data.metadata.salary)}
              </Text>
            </Box>
          )}
          {data.metadata.prestige && (
            <Box className={styles.enrichedData}>
              <Text className={styles.enrichedDataLabel}>Prestige</Text>
              {renderValue(data.metadata.prestige)}
            </Box>
          )}
          {data.metadata.flightRisk && (
            <Box className={styles.enrichedData}>
              <Text className={styles.enrichedDataLabel}>Flight Risk</Text>
              {renderValue(data.metadata.flightRisk)}
            </Box>
          )}
        </Box>
      </Grid>

      {/* Work Experience Section */}
      {dataWithFormattedDescriptions.workExperience.length > 0 && (
        <>
          {/* Work Experience Header - Full Width */}
          <Box className={styles.dividerBoxHeader}>
            <Text className={styles.categoryHeader}>Work Experience</Text>
          </Box>

          {/* Work Experience Entries */}
          {dataWithFormattedDescriptions.workExperience.map((entry, index) => (
            <React.Fragment key={`work-experience-${index}`}>
              {/* Horizontal divider before work experience (except first) */}
              {index > 0 && (
                <Box className={styles.dividerBox}>
                  <Box className={styles.divider} />
                </Box>
              )}

              {/* Two Column Grid for Work Experience Entry */}
              <Grid
                gridColumnGap={`${GRID_COLUMN_GAP}px`}
                templateColumns={GRID_TEMPLATE_COLUMNS}
                width="100%"
                alignItems="start"
              >
                {/* Left side - Work experience details */}
                <Box className={styles.contentBox}>
                  <Text className={styles.sectionDate}>
                    {entry.startDate} - {entry.endDate}
                  </Text>
                  <Text className={styles.sectionTitle}>{entry.role}</Text>
                  <Text className={styles.sectionDetails}>
                    {entry.positionLocation}
                  </Text>
                  <Box
                    className={`${styles.sectionDetails} ${styles.description}`}
                  >
                    {(() => {
                      if (entry.isFormatted) {
                        return renderMarkdownDescription(entry.description);
                      }

                      if (formattingIndices.has(index)) {
                        return <SkeletonText noOfLines={3} spacing="3" />;
                      }

                      if (
                        shouldRenderMarkdownDescription(
                          entry.description,
                          false
                        )
                      ) {
                        return renderMarkdownDescription(entry.description);
                      }

                      return (
                        <Text className={styles.descriptionText}>
                          {entry.description}
                        </Text>
                      );
                    })()}
                  </Box>
                </Box>

                {/* Right side - Enriched data (Gray Card) */}
                <Box className={styles.grayCard}>
                  {entry.jobFamily && (
                    <Box className={styles.enrichedData}>
                      <Text className={styles.enrichedDataLabel}>
                        Job Family
                      </Text>
                      <Text className={styles.enrichedDataValue}>
                        {startCase(entry.jobFamily)}
                      </Text>
                    </Box>
                  )}
                  {entry.seniority && (
                    <Box className={styles.enrichedData}>
                      <Text className={styles.enrichedDataLabel}>
                        Seniority
                      </Text>
                      {renderValue(entry.seniority)}
                    </Box>
                  )}
                  {entry.companyPrestige && (
                    <Box className={styles.enrichedData}>
                      <Text className={styles.enrichedDataLabel}>
                        Company Prestige
                      </Text>
                      {renderValue(entry.companyPrestige)}
                    </Box>
                  )}
                  {entry.tenure && (
                    <Box className={styles.enrichedData}>
                      <Text className={styles.enrichedDataLabel}>Tenure</Text>
                      {renderValue(entry.tenure)}
                    </Box>
                  )}
                  {(entry.salary.min > 0 || entry.salary.max > 0) && (
                    <Box className={styles.enrichedData}>
                      <Text className={styles.enrichedDataLabel}>Salary</Text>
                      <Text className={styles.enrichedDataValue}>
                        {formatSalaryRange(entry.salary)}
                      </Text>
                    </Box>
                  )}
                  {/* Position-level skills hidden for now. */}
                  {/* {entry.skills.length > 0 && (
                    <Box className={styles.enrichedData}>
                      <Text className={styles.enrichedDataLabel}>Skills</Text>
                      <Text className={styles.enrichedDataValue}>
                        {entry.skills
                          .map((skill) => startCase(skill))
                          .join(', ')}
                      </Text>
                    </Box>
                  )} */}
                </Box>
              </Grid>
            </React.Fragment>
          ))}
        </>
      )}

      {/* Education Section */}
      {data.education.length > 0 && (
        <>
          <Box className={styles.dividerBoxHeader}>
            <Text className={styles.categoryHeader}>Education</Text>
          </Box>

          {data.education.map((entry, index) => (
            <React.Fragment key={`education-${index}`}>
              {index > 0 && (
                <Box className={styles.dividerBox}>
                  <Box className={styles.divider} />
                </Box>
              )}

              <Box className={styles.contentBox}>
                <Text className={styles.sectionDate}>
                  {entry.startDate} - {entry.endDate || 'Present'}
                </Text>
                <Text className={styles.sectionTitle}>{entry.institution}</Text>
                <Text className={styles.sectionDetails}>{entry.degree}</Text>
                {entry.fieldOfStudy && (
                  <Text className={styles.sectionDetails}>
                    {entry.fieldOfStudy}
                  </Text>
                )}
                {entry.description && (
                  <Box
                    className={`${styles.sectionDetails} ${styles.description}`}
                  >
                    <Text className={styles.descriptionText}>
                      {entry.description}
                    </Text>
                  </Box>
                )}
              </Box>
            </React.Fragment>
          ))}
        </>
      )}

      {/* Skills Section */}
      {(() => {
        const filteredSkills = (data.skills || []).filter(
          (skill) =>
            skill && typeof skill === 'string' && skill.trim().length > 0
        );
        return filteredSkills.length > 0 ? (
          <>
            <Box className={styles.dividerBoxHeader}>
              <Text className={styles.categoryHeader}>Skills</Text>
            </Box>

            <Box className={styles.contentBox}>
              <Box display="flex" flexWrap="wrap" gap="4px">
                {filteredSkills.map((skill, index) => (
                  <Text
                    key={`skill-${index}`}
                    display="inline-block"
                    padding="0px 5px"
                    borderRadius="md"
                    backgroundColor="white"
                    border="1px solid"
                    borderColor="gray.300"
                    color="#2d426a"
                    fontSize="xs"
                  >
                    {startCase(String(skill))}
                  </Text>
                ))}
              </Box>
            </Box>
          </>
        ) : null;
      })()}

      {/* Publications Section */}
      {data.publications.length > 0 && (
        <>
          <Box className={styles.dividerBoxHeader}>
            <Text className={styles.categoryHeader}>Publications</Text>
          </Box>

          {data.publications.map((entry, index) => (
            <React.Fragment key={`publication-${index}`}>
              {index > 0 && (
                <Box className={styles.dividerBox}>
                  <Box className={styles.divider} />
                </Box>
              )}

              <Box className={styles.contentBox}>
                <Text className={styles.sectionDate}>{entry.date}</Text>
                <Text className={styles.sectionTitle}>{entry.title}</Text>
                <Text className={styles.sectionDetails}>{entry.source}</Text>
                {entry.description && (
                  <Box
                    className={`${styles.sectionDetails} ${styles.description}`}
                  >
                    <Text className={styles.descriptionText}>
                      {entry.description}
                    </Text>
                  </Box>
                )}
              </Box>
            </React.Fragment>
          ))}
        </>
      )}

      {/* Languages Section */}
      {data.languages.length > 0 && (
        <>
          <Box className={styles.dividerBoxHeader}>
            <Text className={styles.categoryHeader}>Languages</Text>
          </Box>

          <Box className={styles.contentBox}>
            {data.languages.map((entry, index) => (
              <Text key={`language-${index}`} className={styles.sectionDetails}>
                {entry.language} - {entry.proficiency}
              </Text>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};
